import { useState } from 'react';
import { useAdminLogs } from '@/hooks/useAdminLogs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Download, Search, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

const AdminLogsPage = () => {
  const {
    adminLogs,
    userActivityLogs,
    loading,
    filterType,
    setFilterType,
    searchQuery,
    setSearchQuery,
    exportLogs,
    refresh
  } = useAdminLogs();

  const [activeTab, setActiveTab] = useState('admin');

  const filteredAdminLogs = adminLogs.filter(log => 
    searchQuery === '' || 
    log.action_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.admin_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUserLogs = userActivityLogs.filter(log =>
    searchQuery === '' ||
    log.activity_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getActionTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      ban_user: 'destructive',
      unban_user: 'default',
      update_plan: 'secondary',
      update_credits: 'secondary',
      delete_user: 'destructive',
      reset_usage: 'default'
    };
    return colors[type] || 'outline';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
              Activity Logs
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor all admin actions and user activity in real-time
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportLogs(activeTab as 'admin' | 'user', 'csv')}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportLogs(activeTab as 'admin' | 'user', 'json')}
            >
              <Download className="mr-2 h-4 w-4" />
              Export JSON
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ban_user">Ban User</SelectItem>
                  <SelectItem value="unban_user">Unban User</SelectItem>
                  <SelectItem value="update_plan">Update Plan</SelectItem>
                  <SelectItem value="update_credits">Update Credits</SelectItem>
                  <SelectItem value="delete_user">Delete User</SelectItem>
                  <SelectItem value="reset_usage">Reset Usage</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="generation">Generation</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs */}
        <Card>
          <CardHeader>
            <CardTitle>Logs Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="admin" className="flex-1">
                    Admin Actions ({filteredAdminLogs.length})
                  </TabsTrigger>
                  <TabsTrigger value="user" className="flex-1">
                    User Activity ({filteredUserLogs.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="admin" className="mt-6 space-y-3">
                  {filteredAdminLogs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12">No admin logs found</p>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredAdminLogs.map((log) => (
                        <div
                          key={log.id}
                          className="p-4 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant={getActionTypeColor(log.action_type) as any}>
                                  {log.action_type.replace(/_/g, ' ').toUpperCase()}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  by {log.admin_email || 'System'}
                                </span>
                              </div>
                              {log.details && Object.keys(log.details).length > 0 && (
                                <div className="mt-2 p-2 bg-muted/50 rounded text-xs font-mono">
                                  {JSON.stringify(log.details, null, 2)}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(log.created_at), 'PPp')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="user" className="mt-6 space-y-3">
                  {filteredUserLogs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-12">No user activity logs found</p>
                  ) : (
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredUserLogs.map((log) => (
                        <div
                          key={log.id}
                          className="p-4 bg-card border border-border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {log.activity_type.replace(/_/g, ' ').toUpperCase()}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  {log.user_email || 'Unknown User'}
                                </span>
                              </div>
                              {log.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {log.description}
                                </p>
                              )}
                              {log.metadata && Object.keys(log.metadata).length > 0 && (
                                <div className="mt-2 p-2 bg-muted/50 rounded text-xs font-mono">
                                  {JSON.stringify(log.metadata, null, 2)}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(log.created_at), 'PPp')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogsPage;
